import React from "react";
import { Select, TextField } from "@c4c-web/ui";
import type {
  IUseAddressFormSetters,
  IUseAddressFormStates,
} from "../../../hooks";
import { getAreaList, getCityList, getZoneList } from "../../../utils";
import type { ICity } from "./address-form.interface";
import type { IFormFieldsProps } from "./AddressFormFields";

interface IQatarFormFieldsProps extends IFormFieldsProps {
  addressFormSetters: IUseAddressFormSetters;
  addressFormStates: IUseAddressFormStates;
  cities: ICity[];
}

export const QatarFormFields: React.FC<IQatarFormFieldsProps> = ({
  addressFormStates,
  addressFormSetters,
  cities,
  errors,
  formData,
  register,
  t,
}) => {
  const {
    qatarStates: { selectedCity, selectedZone, selectedArea },
    errorStates: { cityError, zoneError, areaError },
  } = addressFormStates;

  const cityList = getCityList(cities);
  const zoneList = getZoneList(cities);
  const areaList = getAreaList(cities);

  const handleCityChange = (city: string): void => {
    addressFormSetters.setQatarStates((prevStates) => ({
      ...prevStates,
      selectedCity: city,
      selectedZone: "",
      selectedArea: "",
    }));
  };

  const handleZoneChange = (zone: string): void => {
    addressFormSetters.setQatarStates((prevStates) => ({
      ...prevStates,
      selectedZone: zone,
      selectedArea: "",
    }));
  };

  const handleAreaChange = (area: string): void => {
    addressFormSetters.setQatarStates((prevStates) => ({
      ...prevStates,
      selectedArea: area,
    }));
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex flex-col gap-sm">
        <div>
          <Select
            className="font-semiBold"
            error={cityError}
            helperText={cityError ? t("addressJourney.error.city") : ""}
            label={t("addressJourney.addressForm.city")}
            onValueChange={handleCityChange}
            options={cityList}
            value={selectedCity}
          />
        </div>
        <div className="flex w-full gap-sm">
          <div className="w-[180px]">
            <Select
              className="font-semiBold"
              disabled={!selectedCity}
              error={zoneError}
              helperText={zoneError ? t("addressJourney.error.zone") : ""}
              label={t("addressJourney.addressForm.zone")}
              onValueChange={handleZoneChange}
              options={zoneList?.[selectedCity] || []}
              value={selectedZone}
            />
          </div>
          <div className="w-full">
            <Select
              className="font-semiBold"
              disabled={!selectedZone}
              error={areaError}
              helperText={areaError ? t("addressJourney.error.area") : ""}
              label={t("addressJourney.addressForm.area")}
              onValueChange={handleAreaChange}
              options={areaList?.[selectedZone] || []}
              value={selectedArea}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-sm">
        <div>
          <TextField
            defaultValue={formData?.streetName}
            id="street"
            label={t("addressJourney.addressForm.streetNumber")}
            {...register("streetName", { required: true })}
            error={Boolean(errors.streetName)}
            haveBoldText
            helperText={
              errors.streetName ? t("addressJourney.error.street") : ""
            }
            placeholder={t("addressJourney.addressForm.placeHolders.street")}
          />
        </div>
        <div>
          <TextField
            defaultValue={formData?.appartment}
            id="appartment"
            label={t("addressJourney.addressForm.company")}
            {...register("appartment", { required: true })}
            error={Boolean(errors.appartment)}
            haveBoldText
            helperText={
              errors.appartment ? t("addressJourney.error.company") : ""
            }
            placeholder={t(
              "addressJourney.addressForm.placeHolders.appartment",
            )}
          />
        </div>
        <div>
          <TextField
            defaultValue={formData?.building}
            id="building"
            label={t("addressJourney.addressForm.compound")}
            {...register("building", { required: true })}
            error={Boolean(errors.building)}
            haveBoldText
            helperText={
              errors.building ? t("addressJourney.error.compound") : ""
            }
            placeholder={t("addressJourney.addressForm.placeHolders.building")}
          />
        </div>
      </div>
    </div>
  );
};
